import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { formPlaceholderTradPrefix } from '@/legacy/i18n';
import { useVirtualizer } from '@tanstack/react-virtual';

import SelectAllRow from './SelectAllRow';
import OptionComponent from './Option';
import CreateRow from './CreateRow';
import { useSelect } from '../hooks/useCoreContext';
import SearchInput from './SearchInput';
import { Option } from '@/legacy/types';
import type { CheckboxShape } from '@frekuent/orbit/components';

const VIRTUALIZE_THRESHOLD = 300;
const ITEM_HEIGHT = 48;
const VIRTUAL_LIST_HEIGHT = 200;

const OptionsList = ({
	checkBoxShape,
	searchable,
	isLoadingOptions,
	showScrollbar = true,
}: {
	checkBoxShape?: CheckboxShape;
	searchable?: boolean;
	isLoadingOptions?: boolean;
	showScrollbar?: boolean;
}) => {
	const {
		displayOptions,
		creatable,
		hasSelectAllRow,
		creatableIndex,
		baseIndex,
		inputValue,
		onInputChange,
		options,
		highlightedIndex,
		interactionModeRef,
	} = useSelect();
	const { t } = useTranslation();

	const quantityOptions = displayOptions.length;
	const shouldVirtualize = quantityOptions > VIRTUALIZE_THRESHOLD;

	const parentRef = useRef<HTMLDivElement>(null);

	const virtualizer = useVirtualizer({
		count: shouldVirtualize ? quantityOptions : 0,
		getScrollElement: () => parentRef.current,
		estimateSize: () => ITEM_HEIGHT,
		overscan: 5,
	});

	useEffect(() => {
		if (!shouldVirtualize) return;
		if (interactionModeRef.current !== 'keyboard') return;
		const itemIndex = highlightedIndex - baseIndex;
		if (itemIndex >= 0 && itemIndex < quantityOptions) {
			virtualizer.scrollToIndex(itemIndex, { align: 'auto' });
		}
	}, [
		highlightedIndex,
		shouldVirtualize,
		baseIndex,
		quantityOptions,
		virtualizer,
		interactionModeRef,
	]);

	return (
		<div className='flex flex-col'>
			{searchable && options.length > 0 && (
				<div className='z-10 bg-light'>
					<SearchInput
						searchTerm={inputValue}
						setSearchTerm={onInputChange}
					/>
				</div>
			)}

			<div
				ref={parentRef}
				style={{ height: shouldVirtualize ? `${VIRTUAL_LIST_HEIGHT}px` : undefined }}
				className={`max-h-60 overflow-auto ${searchable ? 'pb-2' : 'py-2'} ${showScrollbar ? 'custom-scrollbar' : 'scrollbar-hidden'}`}
			>
				{hasSelectAllRow && options.length > 0 && (
					<SelectAllRow
						index={0}
						label={
							inputValue?.trim() ? (
								<span>
									{t(`${formPlaceholderTradPrefix}.select_all_filtered`, {
										count: displayOptions.length,
									})}
								</span>
							) : (
								<span>{t(`${formPlaceholderTradPrefix}.select_all`)}</span>
							)
						}
						checkBoxShape={checkBoxShape}
						scope={inputValue?.trim() ? 'visible' : 'global'}
					/>
				)}

				{shouldVirtualize ? (
					<div
						style={{
							height: `${virtualizer.getTotalSize()}px`,
							width: '100%',
							position: 'relative',
						}}
					>
						{virtualizer.getVirtualItems().map((vItem) => (
							<div
								key={vItem.key}
								style={{
									position: 'absolute',
									top: 0,
									left: 0,
									width: '100%',
									height: `${vItem.size}px`,
									transform: `translateY(${vItem.start}px)`,
								}}
							>
								<OptionComponent
									option={displayOptions[vItem.index] as Option}
									index={baseIndex + vItem.index}
									checkBoxShape={checkBoxShape}
								/>
							</div>
						))}
					</div>
				) : (
					displayOptions.map((o: Option, i: number) => (
						<OptionComponent
							key={String(o.value)}
							option={o}
							index={baseIndex + i}
							checkBoxShape={checkBoxShape}
						/>
					))
				)}

				{creatable && <CreateRow index={creatableIndex} />}

				{!creatable && quantityOptions === 0 && !isLoadingOptions && (
					<li className='px-4 py-2 text-tertiary'>{t('form.no_options')}</li>
				)}
			</div>
		</div>
	);
};

export default OptionsList;
